import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';

import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser as CapacitorBrowser } from '@capacitor/browser';
import { Network } from '@capacitor/network';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { DataService } from './services/data.service';
import { AuthService } from './services/auth.service';
// PAUSED: idle service removed — see ticket
// import { IdleService } from './services/idle.service';
import { StorageService } from './services/storage.service';
// PAUSED: auth-timing service removed — see ticket
// import { AuthTimingService } from './services/auth-timing.service';
import { DeviceSessionService } from './services/device-session.service';
import { ImmersiveVideoService } from './services/immersive-video.service';
import { GapSettings } from './config/gapsettings';
import { AppFlowService } from './services/executors/app-flow.service';
import { ModalAriaService } from './services/modal-aria.service';
import { AppIconBadgeService } from './services/notifications/app-icon-badge.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  public environment;
  public _showInfo?: boolean = undefined;
  private menuNavigationInProgress = false;
  private backButtonObserver: MutationObserver | null = null;

  // Red dot markers for unread messages
  public redDotMarks = {
    mobileMenu: false,
    myReferralsMobile: false
  };

  // NHS menu item highlight state
  public highlightedMenuIndex: number | null = null;
  public highlightedMenuColor: string | null = null;

  constructor(
    public router: Router,
    public data: DataService,
    public platform: Platform,
    private changeDetectorRef: ChangeDetectorRef,
    private zone: NgZone,
    // PAUSED: idle service removed — see ticket
    // private idle: IdleService,
    private storageService: StorageService,
    // PAUSED: auth-timing service removed — see ticket
    // private authTimingService: AuthTimingService,
    private deviceSessionService: DeviceSessionService,
    private gapSettings: GapSettings,
    private appFlowService: AppFlowService,
    private translate: TranslateService,
    private immersiveVideo: ImmersiveVideoService,
    private authService: AuthService,
    private modalAria: ModalAriaService,
    private appIconBadge: AppIconBadgeService,
  ) {
    this.environment = environment;
  }

  get showInfo() {
    // if (this.gapSettings.enable_debug_bar !== '1') {
    //   return false;
    // }
    if (this._showInfo === undefined) {
      return this.gapSettings.enable_debug_bar === '1';
    }
    return this._showInfo;
  }

  set showInfo(value: boolean) {
    this._showInfo = value;
  }

  /**
   * Add debug methods to window for testing
   */
  private addDebugMethods(): void {
    const self = this;

    // ======================================
    // DEBUG: Add letter to inbox
    // ======================================
    (window as any).windowDebug = (window as any).windowDebug || {};
    (window as any).addLetter = async (letterTitle?: string, letterBody?: string) => {
      try {
        console.log('🐛 DEBUG: Adding test letter to inbox...');
        
        // Get current letters
        const currentLetters = this.data.letters || [];
        console.log('📬 Current letters count:', currentLetters.length);

        // Generate new letter ID (max existing + 1)
        const maxId = currentLetters.length > 0 
          ? Math.max(...currentLetters.map(l => l.id || 0))
          : 7000;
        const newLetterId = maxId + 1;

        // Create test letter
        const newLetter = {
          id: newLetterId,
          title: letterTitle || `${new Date().toLocaleDateString()} - Test getUBetter team`,
          body: letterBody || `Dear Test User,\n\nThis is a test letter to debug the inbox component and test new message indicators.\n\nYou can customize the title and body by calling:\nwindowDebug.addLetter('Your Title', 'Your Body')\n\nThank you for testing!\n\nThe getUBetter team`,
          read: 0,  // Mark as unread
          read_status: 0,
          accesscode_id: 71393,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          deleted_at: null,
          webform_link: null,
          listing_id: null,
          webform_status: 'none'
        };

        console.log('✉️  New letter to add:', newLetter);

        // Add letter to array
        const updatedLetters = [...currentLetters, newLetter];
        
        // Update letters in UserService (which updates both memory and storage)
        await this.data.userService.updateLetters(updatedLetters);
        
        console.log('✅ Letter added successfully!');
        console.log('📬 Total letters now:', updatedLetters.length);
        console.log('💌 New unread count:', this.data.userService.getUnreadLettersCount());
        
        return newLetter;
      } catch (error) {
        console.error('❌ Error adding letter:', error);
        throw error;
      }
    };

    // Usage example log
    console.log('🐛 DEBUG METHODS AVAILABLE:');
    console.log('  ✓ windowDebug.addLetter() - Add test letter');
    console.log('  ✓ windowDebug.addLetter("Title", "Body") - Add letter with custom title and body');

    // ======================================
    // DEBUG: Navigate to route (for automated testing)
    // ======================================
    (window as any).routeTo = (path: string) => {
      console.log('🐛 DEBUG: Navigating to', path);
      self.zone.run(() => {
        self.router.navigate([`/${path}`]);
      });
    };
    console.log('  ✓ routeTo("smpage/1/1") - Navigate to a specific route');

    // ======================================
    // DEBUG: Highlight menu items for testing
    // ======================================
    (window as any).highlightMenu = (indexOrUrl?: number | string) => {
      if (indexOrUrl !== undefined) {
        self.highlightMenuItem(indexOrUrl, 5000);
      } else {
        // Test with NHS settings (index 9)
        self.highlightMenuItem(9, 5000);
      }
    };
    console.log('  ✓ highlightMenu() - Highlight NHS menu item (test)');
    console.log('  ✓ highlightMenu(9) - Highlight menu item at index 9 (NHS)');
    console.log('  ✓ highlightMenu("/inbox") - Highlight Inbox menu item');
  }

  public menu: any = [
    {
      title: 'Dashboard',
      url: '/home',
      icon: 'home',
      color: 'grey',
      src: '/assets/custom-ion-icons/menu-dashboard.svg',
    },
    {
      title: 'Inbox',
      url: '/inbox',
      icon: 'mail',
      color: 'pink',
      src: '/assets/custom-ion-icons/menu-letters.svg',
    },
    {
      title: 'My recovery',
      url: '/myrecoverytimeline',
      icon: 'medkit',
      src: '/assets/custom-ion-icons/menu-recovery.svg',
      color: '#EBA301'
    },
    {
      title: 'infoZONE',
      url: '/infozone',
      icon: 'information-circle',
      color: '#AC5199',
      src: '/assets/custom-ion-icons/menu-infozones.svg'
    },
    {
      title: 'treatmentZONE',
      url: '/treatmentzone',
      icon: 'body',
      color: '#727DBC',
      src: '/assets/custom-ion-icons/menu-treatment.svg'
    },
    {
      title: 'My referrals',
      url: '/myappointments/home',
      icon: 'list',
      color: '#cecece',
      src: '/assets/custom-ion-icons/menu-appointments.svg',
      isUnread: () => this.redDotMarks.myReferralsMobile
    },
    {
      title: 'feedbackZONE',
      url: '/feedbackzone',
      icon: 'chatbubble-ellipses',
      color: '#0099FF',
      src: '/assets/custom-ion-icons/menu-feedback.svg'
    },
    {
      title: 'My GP Practice',
      url: '/mygp',
      icon: 'person',
      color: '#F1E500',
      src: '/assets/custom-ion-icons/menu-mygp.svg'
    },
    {
      title: 'Settings',
      url: '/settings',
      icon: 'options',
      color: '#8DBD21',
      src: '/assets/custom-ion-icons/menu-settings.svg'
    },
    {
      title: "NHS login settings",
      url: "/nhs-callback/settings",
      icon: "options",
      color: "#FF0000",
      src: '/assets/custom-ion-icons/nhs-settings-icon.svg'
    },
    {
      title: 'Help section',
      url: 'https://getubettersupport.zendesk.com/hc/en-gb',
      icon: 'help-circle',
      color: '#5AD1D1',
      src: '/assets/custom-ion-icons/menu-help.svg',
      external: true
    },
    {
      title: 'Change condition',
      url: '/accesscodes',
      icon: 'medical',
      color: '#808080',
      src: '/assets/custom-ion-icons/menu-conditions.svg'
    }
  ]

  async ngOnInit() {
    this.modalAria.init();

    await this.initializeApp();
    
    // Initialize red dots
    this.updateRedDots();
    
    // Subscribe to letter changes to update red dots
    await this.data.letters$.subscribe(() => {
      this.updateRedDots();
      this.changeDetectorRef.detectChanges();
    });

    // Check for inbox visits to clear red dots
    await setInterval(() => {
      if ((this.data as any).inboxVisited) {
        this.clearInboxRedDots();
        (this.data as any).inboxVisited = false; // Reset flag
      }
    }, 100);
  }

  get code() {
    //if there is no progress setup in this screen then go to setup flow
    if (!this.data.progress)
      this.router.navigateByUrl('/recoverytimeline')

    return this.data.progress.code
  }

  async logout() {
    await this.data.logout(true, true, '', true);
    // Navigation will be handled by data.logout() automatically
  }

  /**
   * Update red dot markers based on unread messages
   */
  private updateRedDots(): void {
    const unreadCount = typeof this.newitems === 'number' ? this.newitems : parseInt(this.newitems.toString());
    const hasUnreadMessages = unreadCount > 0;
    
    this.redDotMarks.mobileMenu = hasUnreadMessages;
    this.redDotMarks.myReferralsMobile = hasUnreadMessages;
    
    console.log('🔴 Red dots updated:', this.redDotMarks, 'Unread count:', unreadCount);
  }

  /**
   * Clear red dots when user visits inbox (called from inbox page)
   */
  public clearInboxRedDots(): void {
    this.redDotMarks.mobileMenu = false;
    console.log('🔴 Inbox red dots cleared');
  }

  /**
   * Universal method to highlight any menu item temporarily
   * Generates highlight color based on the menu item's color
   * @param indexOrUrl - Menu item index (number) or URL (string) to highlight
   * @param durationMs - Duration of highlight in milliseconds (default: 5000)
   */
  public highlightMenuItem(indexOrUrl: number | string, durationMs: number = 5000): void {
    let menuIndex: number = -1;

    // Find menu index by number or URL
    if (typeof indexOrUrl === 'number') {
      menuIndex = indexOrUrl;
    } else if (typeof indexOrUrl === 'string') {
      menuIndex = this.menu.findIndex(item => item.url === indexOrUrl);
    }

    if (menuIndex !== -1 && menuIndex < this.menu.length) {
      const menuItem = this.menu[menuIndex];
      const baseColor = menuItem.color || '#FF0000';
      
      // Generate highlight color based on base color
      const highlightColor = this.generateHighlightColor(baseColor);
      
      // Store highlight info
      this.highlightedMenuIndex = menuIndex;
      this.highlightedMenuColor = highlightColor;
      
      console.log('🔆 Highlighting menu item:', {
        index: menuIndex,
        title: menuItem.title,
        baseColor: baseColor,
        highlightColor: highlightColor,
        durationMs: durationMs
      });
      
      // Force change detection
      this.changeDetectorRef.detectChanges();
      
      // Auto-remove highlight after specified duration
      setTimeout(() => {
        this.highlightedMenuIndex = null;
        this.highlightedMenuColor = null;
        console.log('🔆 Menu highlight cleared');
        this.changeDetectorRef.detectChanges();
      }, durationMs);
    } else {
      console.warn('Menu item not found:', indexOrUrl);
    }
  }

  /**
   * Generate a highlight color that complements the base color
   * Creates a lighter, semi-transparent version suitable for highlighting
   * @param baseColor - Hex color string (e.g., '#FF0000')
   * @returns RGBA color string for highlight
   */
  private generateHighlightColor(baseColor: string): string {
    // Convert hex to RGB
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Create lighter version by increasing brightness and adding transparency
    // Mix with white (255, 255, 255) to lighten
    const lightR = Math.min(255, Math.floor(r + (255 - r) * 0.5));
    const lightG = Math.min(255, Math.floor(g + (255 - g) * 0.5));
    const lightB = Math.min(255, Math.floor(b + (255 - b) * 0.5));

    // Return as RGBA with 0.3 opacity
    return `rgba(${lightR}, ${lightG}, ${lightB}, 0.3)`;
  }

  /**
   * Highlight NHS login settings menu item temporarily
   * Called from AccountdetailsPage to show user where to find NHS settings
   */
  public highlightNHSMenuItem(): void {
    // Use the universal highlight method with NHS menu item URL
    this.highlightMenuItem('/nhs-callback/settings', 5000);
  }

  get newitems(): string|number {
    return this.data.letters ? this.data.letters.filter((e) => !e.read).length : 0
  }

  get newitemsCount(): number {
    return Number(this.newitems) || 0;
  }

  private normalizeMenuLabel(value: string): string {
    if (!value) {
      return '';
    }

    const container = document.createElement('div');
    container.innerHTML = value;

    return container.textContent?.trim() || value;
  }

  label(name): string {
    if (this.data.menu) {
      let found = this.data.menu.find((e) => e.name == name)
      if (found && found.custom)
        return this.normalizeMenuLabel(found.custom)
    }

    return name
  }

  async initializeApp() {
    await this.platform.ready().then(async () => {
      this.updateGlobalBackButtonText();
      this.addDebugMethods();
      this.immersiveVideo.start();
      this.appIconBadge.start();
      // Initialize device info in storage after platform is ready
      
      // await this.platform.backButton.subscribeWithPriority(99999, (processNextHandler) => {
      //   return;
      //   console.log('back button pressed');
      //   if (this.router.routerState.snapshot.url == '/feedbackinput') {
      //     let feedbackinputvisited = this.data.routerHistory.indexOf('/feedbackinput');
      //     if (feedbackinputvisited > -1 && this.data.routerHistory[feedbackinputvisited - 1] != undefined && this.data.routerHistory[feedbackinputvisited - 1] == '/welcomequestions') {
      //       this.data.reinjured();
      //       console.log('back to injured!!!!!!!');
      //       return;
      //     }
      //   }
      //   // let splashVisited = ['/welcomequestions','/feedbackinput','/recoverytimeline'].indexOf(this.data.routerHistory[this.data.routerHistory.length - 1]);
      //   // console.log('splash visited: ',splashVisited);
      //   // console.log('current link: ',this.router.routerState.snapshot.url);
      //   // if(splashVisited > -1 && this.data.routerHistory[this.data.routerHistory.length - 2] == '/splash'){
      //   //   window.history.go(-2);
      //   //   return;
      //   // }
      //   processNextHandler();
      // });
      
      console.log('!!!!!!! base path: ', this.data.basePath);

      await this.appFlowService.init();

      // await Network.getStatus().then(($result) => {
      //   console.log('networkConnection: ', $result);
      //   this.data.networkConnection = $result;
      //   if (this.data.networkConnection.connected == false) {
      //     this.data.presentAlert('Your network is unavailable', '', 'OK', 'Please check your Wi-Fi or data connection.');
      //   }
      // })

      let networkAlertTimer: any = null;
      await Network.addListener('networkStatusChange', status => {
        const wasConnected = this.data.networkConnection?.connected !== false;
        this.data.networkConnection = status;
        if (!status.connected && wasConnected) {
          // Debounce: only show alert if still offline after 2 seconds (avoids false positives on page load/focus change)
          networkAlertTimer = setTimeout(async () => {
            const current = await Network.getStatus();
            if (!current.connected) {
              this.data.presentAlert('Your network is unavailable', '', 'OK', 'Please check your Wi-Fi or data connection.');
            }
          }, 2000);
        } else if (status.connected && networkAlertTimer) {
          clearTimeout(networkAlertTimer);
          networkAlertTimer = null;
        }
      });

      await App.addListener('appStateChange', ({ isActive }) => {

        if (this.gapSettings.isSessionTimeoutEnabled()) {
          console.log('App state change detected, but session timeout is disabled.');
          console.log('session timeout:', this.gapSettings.enable_session_timeout);
        // if user is logged
          if (this.data.isUserLogged) { //WHEN THE USER IS LOGGED IN
            // Perform actions for logged-in users
            if (!isActive) {
              // Set device session service to background mode
              this.deviceSessionService.setBackgroundMode(true);

              // Record background time for session validation
              this.storageService.recordBackgroundTime();
            
            } else {
              // App coming to foreground
              console.log('App coming to foreground');

              // Set device session service to foreground mode
              this.deviceSessionService.setBackgroundMode(false);
              // PAUSED: idle service removed — see ticket
              // this.idle.validateSessionOnResume();

              // Record app foreground activity and wait for completion
              this.deviceSessionService.recordActivity('app_foreground').then(() => {
                // Only validate session after activity is recorded
              }).catch(error => {
                console.error('Error recording foreground activity:', error);
                // Still validate session even if activity recording fails
              });

            }
          }
        }

        if (!isActive) {
          // App going to background
          console.log('App going to background');
          

          if (this.data.ttsMediaPlayer != undefined) {
            this.data.ttsMediaPlayer.pause();
            this.data.ttsMediaPlayer.currentTime = 0;
            for (let i in this.data.ttsMediaPlayerSources) {
              this.data.ttsMediaPlayerSources[i] = '';
            }
            this.changeDetectorRef.detectChanges();
          }
        } else {
          void this.appIconBadge.syncOrClearForCurrentRoute();

          // if we are home and came back from /nhs/settings
          // if(this.router.url == '/home' && this.data.visitedNhsSettingsPage){
          //   // this.data.nhsSettings = false;
          //   this.router.navigateByUrl('/nhs-callback/settings');
          // }
          // GT-1412 MFA Guard: do not hijack navigation while MFA pending (prevents redirect->logout on resume)
          if (!this.authService.isMfaValidated()) {
            console.log('[MFA Guard] Skipping foreground auto-nav while MFA pending', { url: this.router.url });
          } else if (['/accesscodes', '/splash', '/welcomequestions', '/welcomequestionnaire', '/feedbackinput', '/recoverytimeline'].indexOf(this.router.url) == -1 && this.data.isUserLogged == true && this.data.pathway != undefined && this.data.pathway && this.data.day && (this.data.last_progress == undefined || !this.data.last_progress || new Date(this.data.last_progress.created_at).getDate() != new Date().getDate())) {
            if (this.data.pathway.show_traffic_light_page == 0) {
              this.data.updateAsymptomaticPathwayProgress();
            } else {
              if (this.data.pathway.show_timeline_page == 1) {
                this.router.navigateByUrl('/recoverytimeline', { replaceUrl: true });
              } else {
                if (this.data.pathway.show_traffic_light_page == 1) {
                  this.router.navigateByUrl('/feedbackinput', { replaceUrl: true });
                }
              }
            }
          }
        }
      });

      // see the route changing in console.log()

      await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.zone.run(() => {
          // console.log('App Url Open Event: ', event); // Debugging
          let slug: any;
          if (this.data.diag.platform == 'ios') {
            slug = event.url.replace('gub://', '');
          } else {
            slug = event.url.replace('app://com.getubetter.gubapp/', '');
          }
          console.log('App Url Open Event Slug: ', slug); // Debugging
          if (slug) {
            this.router.navigateByUrl(slug);
          } else {
            this.router.navigate(['/login']);
          }

          CapacitorBrowser.close();
        });
      });
    });
  }

  closeMenu() {
    this.data.closeMenu()
  }

  async onMenuItemClick(menuItem: any, index: number) {
    if (!menuItem || !menuItem.url) {
      return;
    }

    // Analytics: Hamburger menu option selected
    this.data.setEventsAnalytics('hamburger_option_selected', 'event', {
      product_zone: 'Navigation',
      menu_option: menuItem.title,
      menu_url: menuItem.url,
      menu_index: index
    });

    // Handle external links
    if (menuItem.external) {
      await CapacitorBrowser.open({
        url: menuItem.url,
        windowName: '_blank'
      });
      return;
    }

    // Prevent duplicate outlet activation from rapid clicks or re-entrant navigation
    if (this.menuNavigationInProgress) {
      return;
    }

    const normalize = (url: string) => (url || '').split('?')[0].split('#')[0].toLowerCase();
    const currentUrl = normalize(this.router.url);
    const targetUrl = normalize(menuItem.url);

    // No-op when already on the destination route
    if (currentUrl === targetUrl) {
      this.closeMenu();
      return;
    }

    this.menuNavigationInProgress = true;
    this.closeMenu();
    try {
      await this.router.navigateByUrl(menuItem.url);
    } finally {
      setTimeout(() => {
        this.menuNavigationInProgress = false;
      }, 150);
    }

    // Special tracking for Dashboard
    if (menuItem.url === '/dashboard') {
      this.data.setEventsAnalytics('dashboard_selected', 'event', {
        product_zone: 'Navigation',
        source: 'hamburger_menu',
        menu_url: menuItem.url,
      });
    }
  }

  labelsAppMenu(input: any) {
    const key = this.resolveMenuTranslationKey(input);
    if (key) {
      return this.translate.instant(key);
    }

    return this.data.labels(input, "AppMenu");
  }

  menuLabel(input: any): string {
    const key = this.resolveMenuTranslationKey(input);
    if (key) {
      return this.translate.instant(key);
    }

    return this.labelsAppMenu(input);
  }

  logoutLabel(): string {
    return this.translate.instant('common.logout');
  }

  private resolveMenuTranslationKey(input: any): string | null {
    if (typeof input !== 'string') {
      return null;
    }

    const normalized = input.trim().toLowerCase();
    const map: Record<string, string> = {
      'dashboard': 'nav.dashboard',
      'inbox': 'nav.inbox',
      'my recovery': 'nav.my_treatment',
      'infozone': 'nav.information',
      'treatmentzone': 'nav.local_treatment',
      'my referrals': 'nav.appointments',
      'feedbackzone': 'nav.feedback',
      'my gp practice': 'nav.my_gp',
      'settings': 'nav.settings',
      'nhs login settings': 'nav.nhs_settings',
      'help section': 'nav.help',
      'change condition': 'nav.change_condition',
      'logout': 'common.logout',
    };

    return map[normalized] || null;
  }

  private updateGlobalBackButtonText(): void {
    const applyText = () => {
      const lang = localStorage.getItem('app_language') === 'cy' ? 'cy' : 'en';
      const text = lang === 'cy' ? this.translate.instant('common.back') : 'Back';
      const buttons = document.querySelectorAll('ion-back-button') as NodeListOf<any>;
      buttons.forEach((button) => {
        button.text = text || (lang === 'cy' ? 'Yn ôl' : 'Back');
      });
    };

    setTimeout(applyText, 0);

    this.translate.onLangChange.subscribe(() => setTimeout(applyText, 0));

    if (!this.backButtonObserver) {
      this.backButtonObserver = new MutationObserver(() => applyText());
      this.backButtonObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  toggleInfo() {
    this._showInfo = false;
    setTimeout(() => {
      this._showInfo = undefined;
    }, 5000)
  }

  ngOnDestroy() {
    if (this.backButtonObserver) {
      this.backButtonObserver.disconnect();
      this.backButtonObserver = null;
    }

    // Clean up auth timing service
    // PAUSED: auth-timing service removed — see ticket
    // this.authTimingService.reset();
  }
}
